<template>
  <div class="notification-config">
    <Loading v-if="loading" />
    <div v-else>
      <div class="notification-config-header">
        <div class="notification-config-header-title">
          <i class="fa-solid fa-bell"></i>
          <div>
            <span class="notification-config-title">Notificações por Módulo</span>
            <span class="notification-config-subtitle">Configure como e quem será notificado em cada ação.</span>
          </div>
        </div>
      </div>
      <br>
      <div class="notification-config-cards">
        <div
          v-for="module in modules"
          :key="module.key"
          class="notification-config-card"
          @click="openModuleModal(module)"
        >
          <div class="notification-config-card-header">
            <div class="notification-config-card-icon">
              <i :class="module.icon"></i>
            </div>
            <div class="notification-config-card-info">
              <span class="notification-config-card-title">{{ module.label }}</span>
              <span class="notification-config-card-description">{{ module.description }}</span>
            </div>
          </div>

          <div class="notification-config-card-actions">
            <div
              v-for="action in module.actions"
              :key="action.actionKey"
              class="notification-config-action-row"
            >
              <div class="notification-config-action-info">
                <i :class="action.actionIcon" :style="{ color: action.actionIconColor }"></i>
                <span>{{ action.actionLabel }}</span>
              </div>
              <div class="notification-config-action-badges">
                <span
                  v-for="channelType in channelTypes"
                  :key="channelType.key"
                  class="notification-config-channel-dot"
                  :class="{ 'notification-config-channel-dot-active': action.channels[channelType.key] && action.channels[channelType.key].enabled }"
                  :title="channelType.label"
                >
                  <i :class="channelType.icon"></i>
                </span>
                <span
                  v-if="action.hasAlerts"
                  class="notification-config-alert-dot"
                  :class="{ 'notification-config-alert-dot-active': action.alerts && action.alerts.length > 0 }"
                  title="Alertas antecipados"
                >
                  <i class="fa-solid fa-clock"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        :title="activeModule ? activeModule.label : ''"
        :width="900"
        :height="700"
        v-if="showModal('notificationModule')"
      >
        <NotificationModuleModal
          v-if="activeModule"
          :key="activeModule.key"
          :module="activeModule"
          :channelTypes="channelTypes"
          :groupsAvailable="groupsAvailable"
          @open-params="openParamsModal"
          @save="buildAndSave"
        />
      </Modal>

      <Modal
        title="Parâmetros Disponíveis"
        :width="900"
        :height="750"
        v-if="showModal('notificationParameters')"
      >
        <ParametersInfo :params="activeParameters" />
      </Modal>
    </div>
  </div>
</template>

<script>
import Modal from "@/ui/forms/Modal";
import Loading from "@/ui/shared/Loading.vue";
import ParametersInfo from "@/components/modules/shared/ParametersInfo.vue";
import NotificationModuleModal from "./NotificationModuleModal.vue";

import locacaoParameters from "@/modules/adm/notification/models/locacaoParameters";
import operacionalParameters from "@/modules/adm/notification/models/operacionalParameters";
import tarefaParameters from "@/modules/adm/notification/models/tarefaParameters";

import NotificationService from "../services/notification.service";
import UserService from "@/modules/adm/user/services/user.service";
import CompanyService from "@/modules/adm/company/services/company.service";

import { mapState, mapMutations, mapGetters } from "vuex";

function buildDefaultChannels() {
  return {
    email: { enabled: false, notifyAll: false, groups: [] },
    push: { enabled: false, notifyAll: false, groups: [] },
    web: { enabled: false, notifyAll: false, groups: [] },
    // Sem botão nesta tela: só preserva o canal configurado pelo nixloc-web ao salvar.
    desktop: { enabled: false, notifyAll: false, groups: [] },
  };
}

function buildDefaultAlerts() {
  return [];
}

export default {
  name: "NotificationConfig",
  components: { Modal, Loading, ParametersInfo, NotificationModuleModal },
  data() {
    return {
      loading: true,
      groupsAvailable: [],
      activeParameters: locacaoParameters,
      activeModule: null,

      channelTypes: [
        { key: "email", label: "E-mail", icon: "fa-solid fa-envelope" },
        { key: "push", label: "App", icon: "fa-solid fa-mobile-screen-button" },
        { key: "web", label: "Navegador", icon: "fa-solid fa-globe" },
      ],

      modules: [
        {
          key: "locacao",
          label: "Locação",
          icon: "fa-sharp fa-solid fa-right-left",
          description: "",
          parametersKey: "locacao",
          actions: [
            {
              actionKey: "aprovar-locacao",
              actionIcon: "fa-solid fa-thumbs-up",
              actionIconColor: "var(--color-success)",
              actionLabel: "Aprovar",
              description: "Disparado quando uma locação é aprovada.",
              template: "", hasAlerts: false,
              alerts: buildDefaultAlerts(), channels: buildDefaultChannels(),
            },
            {
              actionKey: "reprovar-locacao",
              actionIcon: "fa-solid fa-thumbs-down",
              actionIconColor: "var(--color-danger)",
              actionLabel: "Reprovar",
              description: "Disparado quando uma locação é reprovada.",
              template: "", hasAlerts: false,
              alerts: buildDefaultAlerts(), channels: buildDefaultChannels(),
            },
          ],
        },
        {
          key: "operacional",
          label: "Operacional",
          icon: "fa-solid fa-truck-loading",
          description: "",
          parametersKey: "operacional",
          actions: [
            {
              actionKey: "saida-equipamento",
              actionIcon: "fa-solid fa-circle-arrow-right",
              actionIconColor: "#019183",
              actionLabel: "Saída",
              description: "Disparado na saída de equipamentos. Suporta alertas antecipados.",
              template: "", hasAlerts: true,
              alerts: buildDefaultAlerts(), channels: buildDefaultChannels(),
            },
            {
              actionKey: "retorno-equipamento",
              actionIcon: "fa-solid fa-circle-arrow-left",
              actionIconColor: "#3D4EAE",
              actionLabel: "Retorno",
              description: "Disparado no retorno de equipamentos. Suporta alertas antecipados.",
              template: "", hasAlerts: true,
              alerts: buildDefaultAlerts(), channels: buildDefaultChannels(),
            },
          ],
        },
        {
          key: "tarefa",
          label: "Tarefa",
          icon: "fa-solid fa-list-check",
          description: "",
          parametersKey: "tarefa",
          actions: [
            {
              actionKey: "notificar-tarefa",
              actionIcon: "fa-solid fa-bell",
              actionLabel: "Tarefa",
              description: "Alerta sobre tarefas pendentes ou próximas do prazo.",
              template: "", hasAlerts: true,
              alerts: buildDefaultAlerts(), channels: buildDefaultChannels(),
            },
          ],
        },
      ],
    };
  },
  mounted() {
    this.loadGroups();
  },
  computed: {
    ...mapState("user", ["userLogged"]),
    ...mapGetters("generic", ["showModal"]),
  },
  methods: {
    ...mapMutations("generic", ["openModal", "removeLoading"]),

    openModuleModal(module) {
      this.activeModule = module;
      this.openModal("notificationModule");
    },

    openParamsModal(parametersKey) {
      const map = {
        locacao: locacaoParameters,
        operacional: operacionalParameters,
        tarefa: tarefaParameters,
      };
      this.activeParameters = map[parametersKey] || locacaoParameters;
      this.openModal("notificationParameters");
    },

    hasEnabledChannel(action) {
      return Object.keys(action.channels).some((key) => action.channels[key]?.enabled);
    },

    buildAndSave() {
      const invalidAction = this.modules
        .flatMap((module) => module.actions)
        .find((action) => action.hasAlerts && this.hasEnabledChannel(action) && !action.alerts?.length);

      if (invalidAction) {
        this.$toasted.show(
          `Configure ao menos 1 alerta antecipado para "${invalidAction.actionLabel}".`,
          { type: "error" }
        );
        this.removeLoading(["btnSave"]);
        return;
      }

      const payload = [];

      this.modules.forEach((module) => {
        module.actions.forEach((action) => {
          const channels = [];

          Object.keys(action.channels).forEach((key) => {
            const ch = action.channels[key];
            if (!ch?.enabled) return;
            channels.push({
              type: key,
              groups: ch.notifyAll ? ["all"] : (ch.groups?.slice() ?? []),
            });
          });

          if (!channels.length) return;

          const entry = { actionKey: action.actionKey, template: action.template, channels };

          if (action.hasAlerts && action.alerts?.length) {
            entry.alerts = action.alerts.map((a) => ({ time: a.time, unit: a.unit }));
          }

          payload.push(entry);
        });
      });

      NotificationService.updateNotification(payload).then(() => {
        this.removeLoading(["btnSave"]);
      });
    },

    loadGroups() {
      UserService.getAllGroups()
        .then((response) => {
          response.data.content.forEach((item) => {
            this.groupsAvailable.push({ key: item, label: item });
          });
          this.loadConfig();
        })
        .catch((err) => {
          console.error('[loadGroups] erro:', err)
          this.loading = false;
        });
    },

    loadConfig() {
      CompanyService.getById(this.userLogged.companyId)
        .then((response) => {
          const notification = response.data?.content?.notification;
          if (notification) {
            try {
              this.applyConfigFromApi(JSON.parse(notification));
            } catch (err) {
              console.error("[loadConfig][JSON.parse] erro:", err);
            }
          }
          this.loading = false;
        })
        .catch((err) => {
          console.error('[loadConfig] erro:', err)
          this.loading = false;
        });
    },

    applyConfigFromApi(config) {
      if (!Array.isArray(config)) return;

      const actionMap = {};
      this.modules.forEach((module) => {
        module.actions.forEach((action) => {
          actionMap[action.actionKey] = action;
        });
      });

      config.forEach((cfgAction) => {
        const action = actionMap[cfgAction.actionKey];
        if (!action) return;

        this.$set(action, "template", typeof cfgAction.template === "string" ? cfgAction.template : "");

        if (Array.isArray(cfgAction.channels)) {
          const channels = buildDefaultChannels();
          cfgAction.channels.forEach(({ type, groups = [] }) => {
            if (!channels[type]) return;
            const notifyAll = groups.includes("all");
            channels[type] = { enabled: true, notifyAll, groups: notifyAll ? [] : groups.slice() };
          });
          this.$set(action, "channels", channels);
        }

        if (action.hasAlerts) {
          const alerts = Array.isArray(cfgAction.alerts) ? cfgAction.alerts.map((a) => ({ time: a.time, unit: a.unit })) : [];
          this.$set(action, "alerts", alerts);
        }
      });
    },
  },
};
</script>

<style scoped>
.notification-config {
  border-radius: 16px;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.notification-config-header {
  display: flex;
  align-items: center;
}

.notification-config-header-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.notification-config-header-title > i {
  font-size: 18px;
  color: var(--color-brand);
}

.notification-config-title {
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.notification-config-subtitle {
  display: block;
  font-size: 13px;
  color: var(--text-muted);
}

.notification-config-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.notification-config-card {
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 16px 20px;
  background: var(--bg-base);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.15s, border-color 0.15s, transform 0.15s;
}

.notification-config-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.notification-config-card-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.notification-config-card-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(87, 118, 150, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-brand);
  font-size: 16px;
  flex-shrink: 0;
}

.notification-config-card-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.notification-config-card-title {
  font-size: 15px;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notification-config-card-description {
  font-size: 12px;
  color: var(--text-muted);
  font-style: italic;
  line-height: 1.5;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.notification-config-card-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 0 12px;
  border-top: 1px solid var(--border-subtle);
  margin-top: 2px;
}

.notification-config-action-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 0;
}

.notification-config-action-info {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 13px;
  color: var(--text-secondary);
}

.notification-config-action-info i {
  width: 16px;
  text-align: center;
  color: var(--text-muted);
  font-size: 15px;
}

.notification-config-action-info span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notification-config-action-badges {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.notification-config-channel-dot,
.notification-config-alert-dot {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-elevated);
  color: var(--text-muted);
  font-size: 16px;
  border: 1px solid var(--border-subtle);
  transition: background 0.2s, color 0.2s;
}

.notification-config-channel-dot-active {
  background: rgba(87, 118, 150, 0.12);
  color: var(--color-brand);
  border-color: var(--color-brand);
}

.notification-config-alert-dot-active {
  background: rgba(255, 193, 7, 0.15);
  color: var(--color-warning);
  border-color: var(--color-warning);
}

</style>
